/* Good Toy Co. global behaviors.
   Components are custom elements so they init/cleanup themselves (incl. theme-editor section reloads). */
(() => {
  if (window.GTC) return;

  /* ---------- Shipping promise (config from snippets/gtc-shipping-config.liquid) ---------- */
  const readShipping = () => {
    try {
      return JSON.parse(document.getElementById('GtcShipping').textContent);
    } catch (e) {
      return { carrier: 'UPS', thresholdCents: 10000, cutoffHour: 14, timeZone: 'America/Detroit',
        shipSaturday: false, deliveryMin: 1, deliveryMax: 5, noShipDates: [] };
    }
  };

  const Shipping = {
    config: readShipping(),
    /* Wall-clock parts in the warehouse time zone. */
    wall(date = new Date()) {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: this.config.timeZone, hourCycle: 'h23',
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
      }).formatToParts(date).reduce((o, p) => ((o[p.type] = p.value), o), {});
      return {
        ymd: `${parts.year}-${parts.month}-${parts.day}`,
        secs: Number(parts.hour) * 3600 + Number(parts.minute) * 60 + Number(parts.second),
        dateUTC: new Date(Date.UTC(+parts.year, +parts.month - 1, +parts.day)),
      };
    },
    ymd(d) { return d.toISOString().slice(0, 10); },
    isShipDay(dUTC) {
      const dow = dUTC.getUTCDay();
      if (dow === 0 || (dow === 6 && !this.config.shipSaturday)) return false;
      return !this.config.noShipDates.includes(this.ymd(dUTC));
    },
    isBusinessDay(dUTC) {
      const dow = dUTC.getUTCDay();
      return dow !== 0 && dow !== 6 && !this.config.noShipDates.includes(this.ymd(dUTC));
    },
    addDays(dUTC, n) { const d = new Date(dUTC); d.setUTCDate(d.getUTCDate() + n); return d; },
    /* Pre-order: ship date (UTC midnight) when config.preorderFrom is a future YYYY-MM-DD, else null. */
    preorderDate(now = new Date()) {
      const p = this.config.preorderFrom;
      if (!p || !/^\d{4}-\d{2}-\d{2}$/.test(p)) return null;
      const d = new Date(`${p}T00:00:00Z`);
      return d > this.wall(now).dateUTC ? d : null;
    },
    weekOf(dUTC) { return `week of ${this.fmtDay(dUTC, { month: 'short', day: 'numeric' })}`; },
    /* { shipsToday, secondsLeft, shipDate (UTC midnight of ship day), preorder } */
    status(now = new Date()) {
      const pre = this.preorderDate(now);
      if (pre) return { shipsToday: false, secondsLeft: 0, shipDate: pre, preorder: true };
      return this.statusNow(now);
    },
    /* Same-day clock ignoring pre-order mode (products flagged custom.ships_now). */
    statusNow(now = new Date()) {
      const w = this.wall(now);
      const cutoff = this.config.cutoffHour * 3600;
      if (this.isShipDay(w.dateUTC) && w.secs < cutoff) {
        return { shipsToday: true, secondsLeft: cutoff - w.secs, shipDate: w.dateUTC };
      }
      let d = this.addDays(w.dateUTC, 1);
      while (!this.isShipDay(d)) d = this.addDays(d, 1);
      return { shipsToday: false, secondsLeft: 0, shipDate: d };
    },
    addBusinessDays(dUTC, n) {
      let d = new Date(dUTC);
      while (n > 0) { d = this.addDays(d, 1); if (this.isBusinessDay(d)) n -= 1; }
      return d;
    },
    deliveryWindowFrom(shipDate) {
      return [this.addBusinessDays(shipDate, this.config.deliveryMin), this.addBusinessDays(shipDate, this.config.deliveryMax)];
    },
    deliveryWindow(now = new Date()) {
      const { shipDate } = this.status(now);
      return [this.addBusinessDays(shipDate, this.config.deliveryMin), this.addBusinessDays(shipDate, this.config.deliveryMax)];
    },
    fmtDay(dUTC, opts = { weekday: 'short', month: 'short', day: 'numeric' }) {
      return dUTC.toLocaleDateString('en-US', { ...opts, timeZone: 'UTC' });
    },
    fmtCountdown(s) {
      const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
      return [h, m, sec].map((v) => String(v).padStart(2, '0')).join(':');
    },
  };

  const money = (cents) => `$${(cents / 100).toFixed(2)}`;
  window.GTC = { Shipping, money };

  /* ---------- <gtc-ship-clock>: "Ships today if ordered in 02:14:07" ---------- */
  class GtcShipClock extends HTMLElement {
    connectedCallback() {
      this.textEl = this.querySelector('[data-text]') || this;
      this.render();
      this.timer = setInterval(() => this.render(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    render() {
      const st = Shipping.status();
      this.classList.toggle('is-today', st.shipsToday);
      this.classList.toggle('is-preorder', !!st.preorder);
      if (st.preorder) { this.textEl.textContent = `Pre-order now, ships ${Shipping.weekOf(st.shipDate)}`; return; }
      this.textEl.textContent = st.shipsToday
        ? `Ships today if ordered in ${Shipping.fmtCountdown(st.secondsLeft)}`
        : `Order now, ships ${Shipping.fmtDay(st.shipDate, { weekday: 'long' })}`;
    }
  }
  if (!customElements.get('gtc-ship-clock')) customElements.define('gtc-ship-clock', GtcShipClock);

  /* ---------- <gtc-arrival>: delivery window ---------- */
  class GtcArrival extends HTMLElement {
    connectedCallback() {
      const shipsNow = this.hasAttribute('ships-now');
      const st = shipsNow ? Shipping.statusNow() : Shipping.status();
      const text0 = this.querySelector('[data-text]') || this;
      if (st.preorder) {
        text0.innerHTML = `Pre-order: ships the <b>${Shipping.weekOf(st.shipDate)}</b>. Arrives ${Shipping.config.deliveryMin} to ${Shipping.config.deliveryMax} business days after it ships with ${Shipping.config.carrier}.`;
        return;
      }
      const [from, to] = shipsNow ? Shipping.deliveryWindowFrom(st.shipDate) : Shipping.deliveryWindow();
      const ship = st.shipsToday ? 'Order today, <b>ships today</b>'
        : `Ships <b>${Shipping.fmtDay(st.shipDate, { weekday: 'long' })}</b>`;
      const text = this.querySelector('[data-text]') || this;
      text.innerHTML = `${ship}. Arrives <b>${Shipping.fmtDay(from)} to ${Shipping.fmtDay(to)}</b> with ${Shipping.config.carrier}.`;
    }
  }
  if (!customElements.get('gtc-arrival')) customElements.define('gtc-arrival', GtcArrival);

  /* ---------- <gtc-rotator>: rotates [data-slide] children ----------
     attrs: interval (s, default 6), mobile-only (rotate only under 750px; above it all slides show inline) */
  class GtcRotator extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll(':scope [data-slide]')];
      this.dots = [...this.querySelectorAll(':scope [data-go]')];
      this.index = 0;
      if (this.slides.length < 2) return;
      this.ms = Number(this.getAttribute('interval') || 6) * 1000;
      this.mq = this.hasAttribute('mobile-only') ? window.matchMedia('(max-width: 749px)') : null;
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.dots.forEach((d) => d.addEventListener('click', () => { this.show(Number(d.dataset.go)); this.restart(); }));
      this.stop = this.stop.bind(this);
      this.start = this.start.bind(this);
      this.onVis = () => (document.hidden ? this.stop() : this.start());
      ['mouseenter', 'focusin', 'touchstart'].forEach((e) => this.addEventListener(e, this.stop, { passive: true }));
      ['mouseleave', 'focusout', 'touchend'].forEach((e) => this.addEventListener(e, this.start, { passive: true }));
      document.addEventListener('visibilitychange', this.onVis);
      if (this.mq) this.mq.addEventListener('change', this.start);
      this.show(0);
      this.start();
    }
    disconnectedCallback() {
      this.stop();
      document.removeEventListener('visibilitychange', this.onVis);
    }
    active() { return !this.reduced && (!this.mq || this.mq.matches); }
    show(i) {
      this.index = (i + this.slides.length) % this.slides.length;
      this.slides.forEach((s, n) => {
        const on = n === this.index;
        s.classList.toggle('is-active', on);
        if (!this.mq) s.toggleAttribute('inert', !on);
      });
      this.dots.forEach((d, n) => d.setAttribute('aria-current', String(n === this.index)));
    }
    start() { this.stop(); if (this.active() && !document.hidden) this.timer = setInterval(() => this.show(this.index + 1), this.ms); }
    stop() { clearInterval(this.timer); this.timer = null; }
    restart() { this.start(); }
  }
  if (!customElements.get('gtc-rotator')) customElements.define('gtc-rotator', GtcRotator);

  /* ---------- <gtc-tabs>: [role=tab] buttons switch [role=tabpanel] ---------- */
  class GtcTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.tabs.forEach((t, i) => {
        t.addEventListener('click', () => this.select(i));
        t.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            const n = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
            this.select(n);
            this.tabs[n].focus();
          }
        });
      });
    }
    select(i) {
      this.tabs.forEach((t, n) => {
        const on = n === i;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
        const panel = document.getElementById(t.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
    }
  }
  if (!customElements.get('gtc-tabs')) customElements.define('gtc-tabs', GtcTabs);

  /* ---------- Quick add from product cards -> Dawn cart drawer ---------- */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-gtc-add]');
    if (!form) return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.renderContents !== 'function') return;
    event.preventDefault();
    const button = form.querySelector('button[type="submit"]');
    const error = form.querySelector('[data-error]');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (error) error.hidden = true;
    const body = new FormData(form);
    body.append('sections', drawer.getSectionsToRender().map((s) => s.id).join(','));
    body.append('sections_url', window.location.pathname);
    try {
      const res = await fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
        method: 'POST', headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' }, body,
      });
      const data = await res.json();
      if (data.status) {
        if (error) { error.textContent = data.description || data.message || 'Could not add to cart.'; error.hidden = false; }
        return;
      }
      drawer.classList.remove('is-empty');
      drawer.renderContents(data);
    } catch (e) {
      form.removeAttribute('data-gtc-add');
      form.submit();
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  });

  /* ---------- Mega menu: close on outside click / Escape ---------- */
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-shop-open]')) return;
    const keep = e.target.closest('.gtc-nav details');
    document.querySelectorAll('.gtc-nav details[open]').forEach((d) => d !== keep && d.removeAttribute('open'));
  });
  document.addEventListener('keyup', (e) => {
    if (e.key !== 'Escape') return;
    const open = document.querySelector('.gtc-nav details[open]');
    if (!open) return;
    open.removeAttribute('open');
    const burger = document.querySelector(`[data-shop-open][aria-controls="${open.id}"]`);
    (burger && burger.offsetParent ? burger : open.querySelector('summary')).focus();
  });

  /* ---------- Shop panel: tabs, mobile drawer open/close, scroll lock ---------- */
  document.addEventListener('click', (e) => {
    const tab = e.target.closest('[data-shop-tab]');
    if (tab) {
      tab.closest('[role=tablist]').querySelectorAll('[data-shop-tab]').forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        const pane = document.getElementById(t.getAttribute('aria-controls'));
        if (pane) pane.hidden = !on;
      });
      return;
    }
    const opener = e.target.closest('[data-shop-open]');
    if (opener) {
      const panel = document.getElementById(opener.getAttribute('aria-controls'));
      if (panel) { panel.open = true; const c = panel.querySelector('[data-shop-close]'); if (c) c.focus(); }
      return;
    }
    const closer = e.target.closest('[data-shop-close]');
    if (closer) {
      const panel = closer.closest('details');
      panel.open = false;
      const burger = document.querySelector(`[data-shop-open][aria-controls="${panel.id}"]`);
      if (burger) burger.focus();
    }
  });
  document.querySelectorAll('.gtc-shop').forEach((panel) => {
    panel.addEventListener('toggle', () => {
      const burger = document.querySelector(`[data-shop-open][aria-controls="${panel.id}"]`);
      if (burger) burger.setAttribute('aria-expanded', panel.open ? 'true' : 'false');
      document.documentElement.classList.toggle('gtc-lock', panel.open && window.matchMedia('(max-width: 989px)').matches);
    });
  });
  document.addEventListener('keydown', (e) => {
    const tab = e.target.closest && e.target.closest('[data-shop-tab]');
    if (!tab || !['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return;
    const tabs = [...tab.closest('[role=tablist]').querySelectorAll('[data-shop-tab]')];
    const step = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : -1;
    const next = tabs[(tabs.indexOf(tab) + step + tabs.length) % tabs.length];
    e.preventDefault(); next.focus(); next.click();
  });
})();

/* "Goes great with" in the cart (Oct 22): add the paired product's offer, then re-render the drawer in place.
   On the /cart page (no drawer) it reloads. */
document.addEventListener('click', async (e) => {
  const btn = e.target.closest && e.target.closest('[data-gtc-upsell]');
  if (!btn) return;
  e.preventDefault();
  btn.disabled = true; btn.textContent = 'Adding';
  const drawer = document.querySelector('cart-drawer');
  try {
    const res = await fetch(window.Shopify && Shopify.routes ? Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: Number(btn.dataset.gtcUpsell), quantity: 1,
        sections: drawer ? drawer.getSectionsToRender().map((s) => s.id) : [], sections_url: window.location.pathname })
    });
    const json = await res.json();
    if (!res.ok || json.status) throw new Error(json.description || 'add failed');
    if (drawer && json.sections) drawer.renderContents(json); else window.location.reload();
  } catch (err) {
    btn.disabled = false; btn.textContent = 'Try again';
  }
});
